/* eslint-disable react/require-default-props */
import React from 'react';
import {Platform} from 'react-native';
import {get} from 'lodash';
import {VictoryBar, VictoryStack} from 'victory-native';

import Icon from '/component/base/Icon';
import {indent, screenWidth as getScreenWidth} from '/constant/layout.constant';
import {
  green,
  orangeRed,
  pearlGreen,
  pearlWhite,
} from '/theme/color/secondary.color';

import WeightTileGraphLabel from '../WeightTileGraphLabel';

import {styles} from './WeightTileGraph.style';

type WeightProfile = {
  healthyWeightRangeImperial: [number, number];
  currentWeight: number;
};

type WeightTileGraphProps = {
  labels?: string[];
  hidePointer?: boolean;
  weightGraph?: {
    data: {
      weightProfile: WeightProfile;
    };
  };
};

import {MAX_CONTENT_WIDTH} from '/constant/data.constant';

export const WeightTileGraph = ({
  hidePointer,
  labels = [],
  weightGraph = {
    data: {
      weightProfile: {healthyWeightRangeImperial: [160, 190], currentWeight: 0},
    },
  },
}: WeightTileGraphProps) => {
  const default_lower = 160;
  const default_higher = 190;

  const isWeb = Platform.OS === 'web';
  const screenWidth = getScreenWidth();
  const screenWidthVal = isWeb
    ? Math.min(screenWidth, MAX_CONTENT_WIDTH)
    : screenWidth;

  // The WeightTracker card has cardContainer margins: indent * 0.8 on each side
  // The Card component itself has padding: 18 on all sides
  const containerMargins = indent * 0.8 * 2;
  const cardPadding = 18 * 2;
  const availableWidth = screenWidthVal - containerMargins - cardPadding;

  // We'll use 0 padding in VictoryStack to fill the available space exactly
  const NEW_GRAPH_PART_LENGTH = availableWidth / 3;

  let currentWeight = get(
    weightGraph,
    'data.currentWeightLog.weight.weightInPounds',
    0,
  );

  const healthyWeightObj = get(weightGraph, 'data.weightProfile', {
    healthyWeightRangeImperial: [default_lower, default_higher],
    currentWeight,
  });

  const [lowerRange, higherRange]: any[] =
    healthyWeightObj?.healthyWeightRangeImperial?.length > 0
      ? healthyWeightObj.healthyWeightRangeImperial
      : [default_lower, default_higher];

  let pointerPosition = null;
  if (currentWeight > 0) {
    if (currentWeight > lowerRange + higherRange - 8) {
      pointerPosition = availableWidth - 15;
    } else if (currentWeight <= lowerRange) {
      pointerPosition =
        (currentWeight * NEW_GRAPH_PART_LENGTH) / lowerRange - 6;
    } else if (currentWeight >= higherRange) {
      pointerPosition =
        NEW_GRAPH_PART_LENGTH * 2 +
        ((currentWeight - higherRange) * NEW_GRAPH_PART_LENGTH) / lowerRange -
        6;
    } else if (lowerRange < currentWeight && currentWeight < higherRange) {
      pointerPosition =
        NEW_GRAPH_PART_LENGTH +
        ((currentWeight - lowerRange) * NEW_GRAPH_PART_LENGTH) /
          (higherRange - lowerRange) -
        6;
    }
  }

  let leftBarColor;
  if (currentWeight !== 0 && currentWeight < lowerRange) {
    leftBarColor = orangeRed;
  } else {
    leftBarColor = pearlWhite;
  }

  const middleBarColor =
    currentWeight >= lowerRange && currentWeight <= higherRange
      ? green
      : pearlGreen;

  const rightBarColor =
    currentWeight > higherRange
      ? orangeRed
      : default_higher
      ? pearlWhite
      : pearlWhite;

  const barWidth = isWeb ? 22 : 13;
  const stackHeight = isWeb ? 70 : 50;
  const labelY = isWeb ? 45 : 32;

  return (
    <>
      {!hidePointer && currentWeight > 0 && pointerPosition !== null && (
        <Icon
          name="Polygon-1"
          style={[styles.svgContainer, {left: pointerPosition}]}
        />
      )}

      <VictoryStack
        horizontal={true}
        padding={{left: 0, right: 0, top: 0, bottom: 0}}
        height={stackHeight}
        width={availableWidth}>
        <VictoryBar
          data={[{x: 1, y: 1}]}
          cornerRadius={{bottomLeft: 6, bottomRight: 6}}
          style={{data: {width: barWidth, fill: leftBarColor}}}
          labelComponent={
            <WeightTileGraphLabel
              text={
                labels[0] ||
                `< ${lowerRange !== undefined ? lowerRange : default_lower}`
              }
              x={NEW_GRAPH_PART_LENGTH * 0.5}
              y={labelY}
            />
          }
        />
        <VictoryBar
          data={[{x: 1, y: 1}]}
          style={{data: {width: barWidth, fill: middleBarColor}}}
          labelComponent={
            <WeightTileGraphLabel
              text={
                labels[1] ||
                `${lowerRange !== undefined ? lowerRange : default_lower} - ${
                  higherRange !== undefined ? higherRange : 190
                } lbs`
              }
              x={NEW_GRAPH_PART_LENGTH * 1.5}
              y={labelY + 1}
            />
          }
        />
        <VictoryBar
          data={[{x: 1, y: 1}]}
          cornerRadius={{topLeft: 6, topRight: 6}}
          style={{data: {width: barWidth, fill: rightBarColor}}}
          labelComponent={
            <WeightTileGraphLabel
              text={
                labels[2] ||
                `> ${higherRange !== undefined ? higherRange : 190}`
              }
              x={NEW_GRAPH_PART_LENGTH * 2.5}
              y={labelY}
            />
          }
        />
      </VictoryStack>
    </>
  );
};

export default WeightTileGraph;
